import cx from "classnames";
import { FC, SVGProps } from "react";

import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import { ExpandableItems } from "molecules/expandable-items/ExpandableItems";
import { TreatmentCard } from "molecules/treatment-card/TreatmentCard";
import { IfFlag, useFeatureFlag } from "services/Flags";
import {
  getMembershipRewardGroupSubheader,
  getProductTitle,
  getProductTitleQuantity,
} from "services/HelperService";
import { getBillingPeriodIntervalTreatmentSelection } from "utils/billingPeriod.helpers";

export const IncludedMonthlyRewards = ({
  freeMembershipRewards,
  description,
  inactiveMembership,
  svg,
  billingPeriodsBeforeFirstReward,
  billingPeriodInterval = 1,
  membershipBillingPeriodInterval = "MONTH",
  membershipBillingPeriodIntervalCount = 1,
}: {
  freeMembershipRewards: Models<"MembershipRewardModel">[];
  description?: string;
  inactiveMembership?: boolean | null;
  svg: FC<SVGProps<SVGSVGElement>>;
  billingPeriodsBeforeFirstReward?: number | null;
  billingPeriodInterval?: number;
  membershipBillingPeriodInterval?: string;
  membershipBillingPeriodIntervalCount?: number;
}) => {
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  const isV3Enabled = true;

  if (!freeMembershipRewards || freeMembershipRewards.length === 0) {
    return null;
  }

  const periodCount =
    membershipBillingPeriodIntervalCount * billingPeriodInterval;

  const treatmentSelection = getBillingPeriodIntervalTreatmentSelection(
    membershipBillingPeriodIntervalCount,
    membershipBillingPeriodInterval,
    isFlexibleMembershipsEnabled,
    periodCount,
    billingPeriodInterval
  );

  const treatmentSelectionSubheader = getMembershipRewardGroupSubheader(
    billingPeriodsBeforeFirstReward,
    membershipBillingPeriodIntervalCount,
    membershipBillingPeriodInterval
  );

  const shouldShowStartingText =
    billingPeriodsBeforeFirstReward !== null &&
    billingPeriodsBeforeFirstReward !== undefined &&
    billingPeriodsBeforeFirstReward > 1;

  if (isV3Enabled) {
    return (
      <div className="px-4 py-3">
        <div className="rounded-xl border border-light-grey bg-white p-4 shadow-sm">
          <div className="space-y-2">
            <div className="flex flex-row items-center gap-2">
              <Icon
                color={inactiveMembership ? "darkGrey" : "brandColor"}
                size="x2small"
                svg={svg}
                className="flex-shrink-0"
              />
              <span className="text-h4 font-serif font-semibold text-primary">
                Enjoy All Of The Following Every {treatmentSelection}
              </span>
            </div>
            <IfFlag flag="timeDelayedTreatmentGroups">
              {shouldShowStartingText ? (
                <div className="pb-1 text-body2 text-secondary">
                  Starting on your{" "}
                  <span className="text-bold2">
                    {treatmentSelectionSubheader}
                  </span>{" "}
                  as a member
                </div>
              ) : null}
            </IfFlag>
            {description && (
              <div
                className={cx(
                  "pb-2 text-body2",
                  inactiveMembership ? "text-dark-grey" : "text-secondary"
                )}
              >
                {description}
              </div>
            )}
            <div className="divide-border-one divide-y">
              {freeMembershipRewards.map((mr) => {
                const title = getProductTitle(
                  mr.packageListingTitle,
                  mr.packageName
                );
                const quantityText = getProductTitleQuantity(
                  mr.quantity,
                  mr.packageUnitType
                );
                return (
                  <div key={mr.id} className="px-3 py-2.5">
                    <div className="text-left text-body2 text-primary">
                      {quantityText && mr.quantity > 1 && (
                        <span className="font-semibold">{quantityText} </span>
                      )}
                      <span className="font-semibold">{title}</span>
                      {mr.packageDescription && (
                        <p className="mt-1 line-clamp-4 font-normal text-secondary">
                          {mr.packageDescription}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-1 bg-white px-6 py-8">
      <div className="mb-6 text-center">
        <div className="flex w-full justify-center">
          <Icon
            color={inactiveMembership ? "darkGrey" : "brandColor"}
            size="xsmall"
            svg={svg}
          />
        </div>
        <div
          className={cx(
            "py-4 text-sub1 uppercase",
            inactiveMembership ? "text-dark-grey" : "text-brand-color"
          )}
        >
          ENJOY ALL OF THE FOLLOWING <br />
          EVERY {treatmentSelection}
        </div>
        <IfFlag flag="timeDelayedTreatmentGroups">
          <div className="text-secondary">
            Starting on your{" "}
            <span className="text-bold1">{treatmentSelectionSubheader}</span> as
            a member
          </div>
        </IfFlag>
        {description && (
          <div
            className={cx(
              "text-body1",
              inactiveMembership ? "text-dark-grey" : "text-secondary"
            )}
          >
            {description}
          </div>
        )}
      </div>

      <ExpandableItems
        items={freeMembershipRewards.map((mr) => ({
          id: mr.id,
          title:
            getProductTitle(mr.packageListingTitle, mr.packageName) +
            " " +
            getProductTitleQuantity(mr.quantity, mr.packageUnitType),
          description: mr.packageDescription,
        }))}
        disabled={inactiveMembership || null}
      />
    </div>
  );
};
